import React from 'react';
import { Form } from 'antd';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useIntl } from 'react-intl';
import useFormValidation from '../../utils/useFormValidation';
import { options } from '../../config/config';
import { LabelRow, LabelText, CounterOk, CounterOver } from './style';

export interface RichTextInputHandle {
  focus: () => void;
}

interface RichTextInputProps {
  name: string;
  label?: string;
  rules?: string[] | string;
  className?: string;
  min?: number;
  max?: number;
  maxBytes?: number;
  noStyle?: boolean;
}

enum ByteUnit {
  B = 1024,
  KB = 1048576,
}

const formatBytes = (n: number) => {
  if (n < ByteUnit.B) return `${n} B`;
  if (n < ByteUnit.KB) return `${(n / ByteUnit.B).toFixed(1)} KB`;
  return `${(n / ByteUnit.KB).toFixed(2)} MB`;
};

const RichTextInput = React.forwardRef<RichTextInputHandle, RichTextInputProps>(
  ({ name, label, rules = [], min, max, maxBytes, noStyle }, ref) => {
    const intl = useIntl();
    const { formValidations, minMaxValidator } = useFormValidation();

    const ruleKeys = Array.isArray(rules) ? rules : [rules];

    const enforceBytes = typeof maxBytes === 'number';
    const enforceChars =
      !enforceBytes &&
      (typeof max === 'number' || ruleKeys.includes('maxLength'));

    const baseRules = formValidations(ruleKeys);

    const charRules = enforceChars
      ? [
          ...(typeof max === 'number'
            ? [minMaxValidator({ validation: 'maxLength', max })]
            : []),
          ...(typeof min === 'number'
            ? [minMaxValidator({ validation: 'minLength', min })]
            : []),
        ]
      : [];

    const byteRules = enforceBytes
      ? [
          {
            validator: async (_: any, v: string) => {
              const bytes = new TextEncoder().encode(v ?? '').length;
              if (bytes <= (maxBytes as number)) return;
              throw new Error(
                intl.formatMessage(
                  {
                    id: 'validation.max_size',
                    defaultMessage: 'Content must be at most {size}',
                  },
                  { size: formatBytes(maxBytes as number) }
                )
              );
            },
            validateTrigger: 'onChange',
          },
        ]
      : [];

    const value = Form.useWatch(name);
    const html = value ?? '';
    const charLen = html.length;
    const byteLen = new TextEncoder().encode(html).length;

    const overBytes = enforceBytes && byteLen > (maxBytes as number);
    const overChars =
      enforceChars && typeof max === 'number' && charLen > (max as number);
    const over = enforceBytes ? overBytes : enforceChars ? overChars : false;

    const quillRef = React.useRef<ReactQuill>(null);
    React.useImperativeHandle(ref, () => ({
      focus: () => quillRef.current?.getEditor()?.focus(),
    }));

    const counterText = enforceBytes
      ? `${formatBytes(byteLen)} / ${formatBytes(maxBytes as number)}`
      : enforceChars
        ? `${charLen}${typeof max === 'number' ? ` / ${max}` : ''}`
        : '';

    return (
      <Form.Item
        name={name}
        noStyle={noStyle}
        label={
          <LabelRow>
            <LabelText>{label}&nbsp;</LabelText>
            {counterText ? (
              over ? (
                <CounterOver>{counterText}</CounterOver>
              ) : (
                <CounterOk>{counterText}</CounterOk>
              )
            ) : null}
          </LabelRow>
        }
        rules={[...baseRules, ...(enforceBytes ? byteRules : charRules)]}
        valuePropName="value"
        getValueFromEvent={(content) => content}
        validateTrigger="onChange"
      >
        <ReactQuill
          ref={quillRef}
          theme={'snow'}
          modules={{ toolbar: options.toolbarOptions }}
        />
      </Form.Item>
    );
  }
);

RichTextInput.displayName = 'RichTextInput';
export default RichTextInput;
